// BUILD-042 UAT local-usability cleanup (2026-10-05 decision) — shared, small presentation
// helpers used across the real (non-preview) HR screens. No data-fetching, no authorization
// logic — purely formatting of already-authorized data this app already has.
//
// Decision requirements this file serves:
//   #3 "Human-readable attribution" — resolve an employeeId to a display name using data the
//      caller ALREADY has (the admin employee directory, or a small explicit name map a screen
//      already fetched) — never a new mutating-resolver call, never a second identity source.
//   #4 "Audit presentation" — Arizona/local date-time to the minute, with timezone clarity,
//      preserving the exact stored timestamp (never relabeled, only formatted for display).
//
// October 10 22:28 Phoenix addendum — platform-wide MM/DD/YYYY (four-digit year) correction,
// authorized per docs/superpowers/decisions/
//   2026-10-07-build-042-owner-led-onboarding-documents-and-test-roster.md. Owner requested
// numeric MM/DD/YYYY for every human-facing date, including hire dates, in place of the mixed
// "Jan 15, 2026"/raw-ISO formats this file and the rest of HR previously produced. This EXTENDS
// the existing correct date-only/timezone-day-shift handling already in this file (the noon-
// anchored parse below predates this addendum and is NOT changed) — only the display OPTIONS
// passed to toLocaleDateString/Intl change, from a short-month style to numeric MM/DD/YYYY. The
// stored ISO/date-only value, the timezone-day-shift protection, and the timestamp's time-of-day
// and timezone label are all preserved exactly as before.

// October 12 05:46 Phoenix batch — the Owner-fixed order is MM/DD/YYYY for every browser. Passing
// `undefined` as the locale made the day/month order follow the browser (en-GB renders DD/MM), so
// the DATE portion is pinned to en-US. Only the date-part locale changes: the time-of-day, the
// browser-local timezone label, the noon-anchored date-only parse, and every stored value are
// unchanged. Native <input type="date"> controls are browser-rendered and are not touched here.
const HR_DATE_LOCALE = 'en-US';

/**
 * Resolve a display name for an employeeId from a name map the caller already has (e.g. the
 * admin employee directory keyed by employeeId -> name|null, or a one-off map built from a
 * screen's own already-fetched data). Returns an honest fallback — never invents a name:
 *   - `null`/undefined employeeId -> 'Unknown'  (no identity recorded at all)
 *   - employeeId present but no resolvable name -> 'Unresolved (<first 8 chars of id>…)'
 *     This is distinct from "Unknown": the identity IS recorded, it just could not be resolved
 *     to a name through the directory this screen has — e.g. a deactivated/removed Employee.
 */
export function resolveDisplayName(
  employeeId: string | null | undefined,
  nameMap: Record<string, string | null | undefined> | null | undefined
): string {
  if (!employeeId) return 'Unknown';
  const name = nameMap ? nameMap[employeeId] : null;
  if (name && name.trim()) return name;
  return `Unresolved (${employeeId.slice(0, 8)}…)`;
}

/** The full id, for the expandable "technical details" affordance — never the primary label. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return `${id.slice(0, 8)}…`;
}

/**
 * Format an ISO/stored timestamp as a readable LOCAL date-time to the minute, with an explicit
 * timezone abbreviation so "local" is never ambiguous to someone reading a screenshot or export
 * from a different timezone. The STORED value (ISO string) is always preserved unmodified
 * alongside this — this function only ever produces a presentation string, never a value fed
 * back into a write or a comparison.
 */
export function formatLocalDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso; // never hide a genuinely malformed value
  // October 10 22:28 addendum: numeric MM/DD/YYYY (four-digit year), not a short-month style —
  // only the date-portion OPTIONS changed; the time-of-day and timezone label below are
  // unaffected.
  const datePart = d.toLocaleDateString(HR_DATE_LOCALE, { year: 'numeric', month: '2-digit', day: '2-digit' });
  const timePart = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  // Intl.DateTimeFormat's timeZoneName gives the correct abbreviation for the browser's actual
  // local zone (e.g. "MST"/"PDT") rather than a hardcoded "Arizona" label that would be wrong
  // for anyone not actually in that zone — "local, clearly labeled" per the decision, not
  // "always Arizona regardless of who is looking."
  let tz = '';
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(d);
    tz = parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch { /* tz abbreviation is a nice-to-have, never required */ }
  return `${datePart}, ${timePart}${tz ? ' ' + tz : ''}`;
}

/** Just the date (no time) — used where only the day matters (due dates, anchor dates, hire
 *  dates). Accepts either a bare date-only string (YYYY-MM-DD) or a full ISO instant. */
export function formatLocalDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // Date-only values (YYYY-MM-DD) must not be re-parsed as UTC midnight and then shifted by the
  // browser's local offset (the classic "shows one day earlier" bug) — append a local-noon time
  // component before parsing so the display date always matches the stored calendar date. This
  // protection predates the October 10 22:28 addendum and is UNCHANGED by it — only the
  // toLocaleDateString options below move from a short-month style to numeric MM/DD/YYYY.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso; // never hide a genuinely malformed value
  return d.toLocaleDateString(HR_DATE_LOCALE, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

// ── BUILD-042 Activity Log — explicit America/Phoenix presentation (2026-10-15 decision) ──────────
// The Activity Log must show every timestamp in America/Phoenix regardless of the viewer's browser
// zone (the generic formatLocalDateTime above intentionally uses the viewer's LOCAL zone and is
// unchanged — other HR screens rely on that). These two helpers pin the zone to America/Phoenix via
// Intl options; they never modify the stored/API ISO instant, only present it. A malformed/absent
// value returns an honest 'unavailable' / '—', never an invented time. Both render the SAME instant.
const HR_ACTIVITY_TIME_ZONE = 'America/Phoenix';

/** Row header: MM/DD/YYYY to the minute, in America/Phoenix, labelled MST. */
export function formatPhoenixDateTimeMinute(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Time unavailable';
  const date = d.toLocaleDateString(HR_DATE_LOCALE, { timeZone: HR_ACTIVITY_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
  const time = d.toLocaleTimeString(HR_DATE_LOCALE, { timeZone: HR_ACTIVITY_TIME_ZONE, hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time} MST`;
}

/** Expanded detail: MM/DD/YYYY with seconds, in America/Phoenix, labelled MST (same instant as the row). */
export function formatPhoenixDateTimeSeconds(iso: string | null | undefined): string {
  if (!iso) return 'Time unavailable';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Time unavailable';
  const date = d.toLocaleDateString(HR_DATE_LOCALE, { timeZone: HR_ACTIVITY_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
  const time = d.toLocaleTimeString(HR_DATE_LOCALE, { timeZone: HR_ACTIVITY_TIME_ZONE, hour: 'numeric', minute: '2-digit', second: '2-digit' });
  return `${date}, ${time} MST`;
}
